import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock, Calendar, ChevronRight, ChevronLeft, User, Share2, BookOpen, Tag, Facebook, Twitter, Linkedin, Copy, Check, Globe, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { SafeHtml } from "@/components/SafeHtml";
import BlogComments from "@/components/BlogComments";
import NewsletterSignup from "@/components/NewsletterSignup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const { postSlug } = useParams();
  const [posts, setPosts] = useState([]);
  const [singlePost, setSinglePost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [toc, setToc] = useState([]);
  const [sliderIndex, setSliderIndex] = useState(0);
  const sliderRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Pobierz strony do slidera
        const sitesRes = await axios.get(`${API}/public/sites-slider`);
        setSites(sitesRes.data);

        if (postSlug) {
          const res = await axios.get(`${API}/public/blog/${postSlug}`);
          setSinglePost(res.data);
          
          // Pobierz powiazane posty
          const relRes = await axios.get(`${API}/public/blog/related/${postSlug}`);
          setRelatedPosts(relRes.data);
          
          // Generuj spis tresci z naglowkow
          if (res.data.content) {
            const headings = [];
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = res.data.content;
            tempDiv.querySelectorAll('h2, h3').forEach((h, i) => {
              const id = `heading-${i}`;
              h.id = id;
              headings.push({
                id,
                text: h.textContent,
                level: h.tagName.toLowerCase()
              });
            });
            setToc(headings);
          }
        } else {
          const res = await axios.get(`${API}/public/blog?limit=20`);
          setPosts(res.data);
        }
      } catch (err) {
        console.error("Blad pobierania postow:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [postSlug]);

  // Auto-scroll slider
  useEffect(() => {
    if (sites.length <= 7) return;
    const interval = setInterval(() => {
      setSliderIndex(prev => (prev + 1) % Math.ceil(sites.length / 7));
    }, 4000);
    return () => clearInterval(interval);
  }, [sites.length]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnSocial = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(singlePost?.title || '');
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const readingTime = singlePost?.content ? Math.ceil(singlePost.content.replace(/<[^>]+>/g, '').split(/\s+/).length / 200) : 0;

  const prevSlide = () => setSliderIndex(prev => Math.max(0, prev - 1));
  const nextSlide = () => setSliderIndex(prev => Math.min(Math.ceil(sites.length / 7) - 1, prev + 1));

  // Sites Slider Component
  const SitesSlider = () => {
    if (sites.length === 0) return null;
    const visibleSites = sites.slice(sliderIndex * 7, sliderIndex * 7 + 7);
    
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm mt-6">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-2">
          <Globe className="w-4 h-4" /> Polecane strony
        </h4>
        <div className="space-y-2">
          {visibleSites.map((site, i) => (
            <Link 
              key={site.id || i} 
              to={`/shop-public?site=${site.id}`}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors group"
            >
              {site.logo ? (
                <img src={site.logo} alt={site.name} loading="lazy" decoding="async" className="w-6 h-6 rounded object-cover" />
              ) : (
                <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center">
                  <Globe className="w-3 h-3 text-blue-600" />
                </div>
              )}
              <span className="text-sm text-slate-700 group-hover:text-blue-600 transition-colors truncate flex-1">
                {site.name}
              </span>
              <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-blue-500 transition-colors" />
            </Link>
          ))}
        </div>
        {sites.length > 7 && (
          <div className="flex justify-center gap-2 mt-4 pt-3 border-t border-slate-100">
            <button 
              onClick={prevSlide} 
              disabled={sliderIndex === 0}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-slate-500" />
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.ceil(sites.length / 7) }).map((_, i) => (
                <div 
                  key={i} 
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${i === sliderIndex ? 'bg-blue-600' : 'bg-slate-200'}`} 
                />
              ))}
            </div>
            <button 
              onClick={nextSlide} 
              disabled={sliderIndex >= Math.ceil(sites.length / 7) - 1}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Widok pojedynczego posta
  if (postSlug && singlePost) {
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": (singlePost.title || "").substring(0, 110),
      "description": singlePost.meta_description || singlePost.excerpt || "",
      "image": singlePost.featured_image ? [singlePost.featured_image] : undefined,
      "datePublished": singlePost.published_at,
      "dateModified": singlePost.updated_at || singlePost.published_at,
      "author": {
        "@type": "Person",
        "name": singlePost.author_name || "Epidemos AI"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Epidemos",
        "logo": {
          "@type": "ImageObject",
          "url": "https://epidemos.pl/og-image.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://epidemos.pl/blog/${singlePost.slug || postSlug}`
      },
      "keywords": singlePost.tags || undefined
    };
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {"@type": "ListItem", "position": 1, "name": "Strona główna", "item": "https://epidemos.pl/"},
        {"@type": "ListItem", "position": 2, "name": "Blog", "item": "https://epidemos.pl/blog"},
        {"@type": "ListItem", "position": 3, "name": singlePost.title, "item": `https://epidemos.pl/blog/${singlePost.slug || postSlug}`}
      ]
    };

    // === GEO/AEO: ekstrakcja FAQ z content (heurystyka <h3>?</h3><p>...</p>) ===
    const extractFaqPairs = (html) => {
      if (!html) return [];
      const pairs = [];
      const re = /<h3[^>]*>([^<]*\?)\s*<\/h3>\s*<p[^>]*>([\s\S]*?)<\/p>/gi;
      let m;
      while ((m = re.exec(html)) !== null) {
        const q = m[1].replace(/<[^>]+>/g, "").trim();
        const a = m[2].replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
        if (q && a && q.length > 5 && a.length > 20) {
          pairs.push({ question: q, answer: a.substring(0, 500) });
        }
        if (pairs.length >= 10) break;
      }
      return pairs;
    };
    const faqPairs = extractFaqPairs(singlePost.content || "");
    const faqSchema = faqPairs.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqPairs.map(p => ({
        "@type": "Question",
        "name": p.question,
        "acceptedAnswer": { "@type": "Answer", "text": p.answer }
      }))
    } : null;

    // === GEO/AEO: HowTo Schema dla tytulow "Jak..." ===
    const isHowTo = /^(jak|how to)\s/i.test((singlePost.title || "").trim());
    const extractSteps = (html) => {
      if (!html) return [];
      const steps = [];
      const re = /<(?:li|h3)[^>]*>([\s\S]*?)<\/(?:li|h3)>/gi;
      let m;
      while ((m = re.exec(html)) !== null) {
        const text = m[1].replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
        if (text && text.length > 20 && text.length < 300) steps.push(text);
        if (steps.length >= 8) break;
      }
      return steps;
    };
    const howToSteps = isHowTo ? extractSteps(singlePost.content || "") : [];
    const howToSchema = (isHowTo && howToSteps.length >= 3) ? {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": singlePost.title,
      "description": singlePost.meta_description || singlePost.excerpt || "",
      "image": singlePost.featured_image ? [singlePost.featured_image] : undefined,
      "step": howToSteps.map((s, i) => ({
        "@type": "HowToStep", "position": i + 1, "text": s
      }))
    } : null;

    // === GEO/AEO: Speakable dla TL;DR (Google Asystent / voice search) ===
    const tldrMatch = (singlePost.content || "").match(/<div[^>]*class="[^"]*tldr[^"]*"[^>]*>([\s\S]*?)<\/div>/i);
    const hasTldr = !!tldrMatch;

    // === GEO/AEO: Speakable dla strony (TL;DR + first paragraph) ===
    const speakableSchema = hasTldr ? {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": [".tldr", ".article-content h2", ".article-content p:first-of-type"]
      },
      "url": `https://epidemos.pl/blog/${singlePost.slug || postSlug}`
    } : null;

    // === GEO/AEO: Wzbogac Article Schema o mentions/about z tagow ===
    const tagList = (singlePost.tags || "").split(",").map(t => t.trim()).filter(Boolean);
    if (tagList.length > 0) {
      articleSchema.about = tagList.slice(0, 5).map(t => ({ "@type": "Thing", "name": t }));
      articleSchema.keywords = tagList.join(", ");
    }
    if (faqPairs.length >= 3) {
      // Sygnal dla Google ze artykul zawiera FAQ
      articleSchema.hasPart = faqPairs.slice(0, 5).map(p => ({
        "@type": "Question", "name": p.question
      }));
    }
    // Update <title>, meta description, canonical w document.head dla CSR-aware crawlers
    if (typeof document !== "undefined") {
      document.title = (singlePost.meta_title || singlePost.title || "Blog Epidemos");
      const ensureMeta = (name, content, attr = "name") => {
        if (!content) return;
        let el = document.querySelector(`meta[${attr}="${name}"]`);
        if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
        el.setAttribute("content", content);
      };
      ensureMeta("description", singlePost.meta_description || singlePost.excerpt || "");
      ensureMeta("og:title", singlePost.title, "property");
      ensureMeta("og:description", singlePost.meta_description || singlePost.excerpt, "property");
      ensureMeta("og:image", singlePost.featured_image, "property");
      ensureMeta("og:type", "article", "property");
      ensureMeta("og:url", `https://epidemos.pl/blog/${singlePost.slug || postSlug}`, "property");
      ensureMeta("article:published_time", singlePost.published_at, "property");
      ensureMeta("article:author", singlePost.author_name || "Epidemos AI", "property");
      let canonical = document.querySelector("link[rel='canonical']");
      if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
      canonical.setAttribute("href", `https://epidemos.pl/blog/${singlePost.slug || postSlug}`);
    }

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Header onLogin={() => window.location.href = "/login"} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
        {faqSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
        )}
        {howToSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
          />
        )}
        {speakableSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(speakableSchema) }}
          />
        )}

        {singlePost.featured_image && (
          <div className="pt-16 bg-slate-900">
            <div className="max-w-6xl mx-auto">
              <div className="aspect-[21/9] overflow-hidden">
                <img src={singlePost.featured_image} alt={singlePost.title} loading="lazy" decoding="async" className="w-full h-full object-cover opacity-80" />
              </div>
            </div>
          </div>
        )}

        <article className={`${singlePost.featured_image ? 'pt-0' : 'pt-24'} pb-20`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              
              <aside className="hidden lg:block lg:col-span-1">
                <div className="sticky top-24">
                  <Link to="/blog" className="inline-flex items-center text-sm text-slate-500 hover:text-blue-600 mb-6 transition-colors">
                    <ArrowLeft className="w-4 h-4 mr-1.5" /> Wszystkie artykuly
                  </Link>
                  
                  {toc.length > 0 && (
                    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-2">
                        <BookOpen className="w-4 h-4" /> Spis tresci
                      </h4>
                      <nav className="space-y-2">
                        {toc.map((item, i) => (
                          <a key={i} href={`#${item.id}`} className={`block text-sm hover:text-blue-600 transition-colors ${item.level === 'h3' ? 'pl-4 text-slate-500' : 'font-medium text-slate-700'}`}>
                            {item.text}
                          </a>
                        ))}
                      </nav>
                    </div>
                  )}

                  <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-2">
                      <Share2 className="w-4 h-4" /> Udostepnij
                    </h4>
                    <div className="flex gap-2">
                      <button onClick={() => shareOnSocial('facebook')} className="flex-1 p-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                        <Facebook className="w-4 h-4 mx-auto" />
                      </button>
                      <button onClick={() => shareOnSocial('twitter')} className="flex-1 p-2.5 rounded-lg bg-sky-500 text-white hover:bg-sky-600 transition-colors">
                        <Twitter className="w-4 h-4 mx-auto" />
                      </button>
                      <button onClick={() => shareOnSocial('linkedin')} className="flex-1 p-2.5 rounded-lg bg-blue-700 text-white hover:bg-blue-800 transition-colors">
                        <Linkedin className="w-4 h-4 mx-auto" />
                      </button>
                      <button onClick={copyLink} className="flex-1 p-2.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">
                        {copied ? <Check className="w-4 h-4 mx-auto text-green-600" /> : <Copy className="w-4 h-4 mx-auto" />}
                      </button>
                    </div>
                  </div>

                  <SitesSlider />
                </div>
              </aside>

              <main className="lg:col-span-3">
                <div className={`bg-white rounded-3xl ${singlePost.featured_image ? '-mt-20 relative z-10' : ''} shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden`}>
                  <div className="p-8 sm:p-10 lg:p-12">
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6">
                      <span className="flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-full">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {singlePost.published_at ? new Date(singlePost.published_at).toLocaleDateString('pl-PL', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                      </span>
                      <span className="flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-full">
                        <Clock className="w-4 h-4 text-slate-400" />
                        {readingTime} min czytania
                      </span>
                      {singlePost.author_name && (
                        <span className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full">
                          <User className="w-4 h-4" />
                          {singlePost.author_name}
                        </span>
                      )}
                    </div>

                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight tracking-tight">
                      {singlePost.title}
                    </h1>

                    {singlePost.tags && (
                      <div className="flex flex-wrap gap-2 mb-8 pb-8 border-b border-slate-100">
                        {singlePost.tags.split(',').map((tag, i) => (
                          <Badge key={i} variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
                            <Tag className="w-3 h-3 mr-1" />{tag.trim()}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <SafeHtml
                      className="article-content prose prose-lg prose-slate max-w-none prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-slate-900 prose-h2:text-2xl prose-h2:sm:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:pb-3 prose-h2:border-b prose-h2:border-slate-200 prose-h3:text-xl prose-h3:sm:text-2xl prose-h3:mt-8 prose-h3:mb-4 prose-h3:text-slate-800 prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-5 prose-a:text-blue-600 prose-a:font-medium prose-a:no-underline prose-a:border-b prose-a:border-blue-200 prose-strong:text-slate-900 prose-ul:my-6 prose-ul:space-y-2 prose-ol:my-6 prose-li:text-slate-700 prose-li:marker:text-blue-500 prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:bg-blue-50 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-xl prose-blockquote:not-italic prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-img:rounded-xl prose-img:shadow-lg [&_.tldr]:p-5 [&_.tldr]:mb-8 [&_.tldr]:bg-blue-50 [&_.tldr]:border-l-4 [&_.tldr]:border-blue-500 [&_.tldr]:rounded-r-xl [&_.tldr]:text-slate-800 [&_table]:w-full [&_table]:my-8 [&_table]:border-collapse [&_th]:bg-slate-100 [&_th]:p-3 [&_th]:text-left [&_th]:border [&_th]:border-slate-200 [&_td]:p-3 [&_td]:border [&_td]:border-slate-200"
                      html={singlePost.content}
                    />

                    <div className="mt-12 pt-8 border-t border-slate-100">
                      <div className="flex items-center gap-4 p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-blue-500/25">
                          {singlePost.author_name ? singlePost.author_name.charAt(0).toUpperCase() : 'E'}
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Autor</p>
                          <p className="font-bold text-slate-900 text-lg">{singlePost.author_name || 'Redakcja Epidemos'}</p>
                          <p className="text-sm text-slate-500">Ekspert ds. content marketingu i SEO</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-12">
                      <NewsletterSignup source="blog-post" />
                    </div>

                    <BlogComments slug={singlePost.slug || postSlug} postTitle={singlePost.title} />

                    {/* Polecane wpisy */}
                    {relatedPosts.length > 0 && (
                      <div className="mt-12 pt-8 border-t border-slate-100">
                        <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                          <BookOpen className="w-5 h-5 text-blue-600" /> Polecane artykuly
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          {relatedPosts.map((rp) => (
                            <Link 
                              key={rp.id} 
                              to={`/blog/${rp.slug || rp.id}`}
                              className="group bg-slate-50 rounded-xl overflow-hidden hover:shadow-lg transition-all"
                            >
                              {rp.featured_image && (
                                <div className="aspect-video bg-slate-200 overflow-hidden">
                                  <img src={rp.featured_image} alt={rp.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                </div>
                              )}
                              <div className="p-4">
                                <h4 className="font-semibold text-slate-800 text-sm line-clamp-2 group-hover:text-blue-600 transition-colors">
                                  {rp.title}
                                </h4>
                                <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {rp.published_at ? new Date(rp.published_at).toLocaleDateString('pl-PL') : ''}
                                </p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-10 p-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl text-white text-center">
                      <h3 className="text-xl font-bold mb-2">Chcesz wiedziec wiecej?</h3>
                      <p className="text-blue-100 mb-6">Dolacz do Epidemos i korzystaj z profesjonalnych narzedzi content marketingu</p>
                      <div className="flex justify-center gap-3">
                        <Link to="/register">
                          <Button className="bg-white text-blue-700 hover:bg-blue-50 shadow-lg">Zaloz konto za darmo</Button>
                        </Link>
                        <Link to="/shop-public">
                          <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">Zobacz marketplace</Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:hidden mt-6 flex gap-2">
                  <button onClick={() => shareOnSocial('facebook')} className="flex-1 p-3 rounded-xl bg-blue-600 text-white"><Facebook className="w-5 h-5 mx-auto" /></button>
                  <button onClick={() => shareOnSocial('twitter')} className="flex-1 p-3 rounded-xl bg-sky-500 text-white"><Twitter className="w-5 h-5 mx-auto" /></button>
                  <button onClick={() => shareOnSocial('linkedin')} className="flex-1 p-3 rounded-xl bg-blue-700 text-white"><Linkedin className="w-5 h-5 mx-auto" /></button>
                  <button onClick={copyLink} className="flex-1 p-3 rounded-xl bg-slate-200 text-slate-700">{copied ? <Check className="w-5 h-5 mx-auto text-green-600" /> : <Copy className="w-5 h-5 mx-auto" />}</button>
                </div>
              </main>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    );
  }

  // Widok listy postow
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header onLogin={() => window.location.href = "/login"} />

      <div className="pt-24 pb-12 px-4 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-white/20 text-white border-0 mb-4">Blog Epidemos</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Wiedza o content marketingu</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">Artykuly o SEO, AI, content marketingu i budowaniu marki online.</p>
        </div>
      </div>

      <div className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 rounded-full border-4 border-blue-600 border-t-transparent animate-spin mx-auto mb-4" />
              <p className="text-slate-500">Ladowanie artykulow...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-10 h-10 text-slate-300" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Wkrotce nowe artykuly</h2>
              <p className="text-slate-500 mb-6">Pracujemy nad wartosciowymi tresciami.</p>
              <Button variant="outline" asChild><Link to="/">Wroc na strone glowna</Link></Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, i) => (
                <Link key={post.id} to={`/blog/${post.slug || post.id}`} className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100 ${i === 0 ? 'md:col-span-2 lg:col-span-2' : ''}`}>
                  {post.featured_image && (
                    <div className={`${i === 0 ? 'aspect-[21/9]' : 'aspect-video'} bg-slate-100 overflow-hidden`}>
                      <img src={post.featured_image} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-slate-400 mb-3">
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{post.published_at ? new Date(post.published_at).toLocaleDateString('pl-PL') : ''}</span>
                      {post.author_name && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{post.author_name}</span>}
                    </div>
                    <h2 className={`${i === 0 ? 'text-2xl' : 'text-lg'} font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2`}>{post.title}</h2>
                    <p className="text-slate-500 text-sm line-clamp-3 mb-4">{post.excerpt}</p>
                    <div className="flex items-center text-sm text-blue-600 font-semibold">Czytaj artykul <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" /></div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
